import { defineStore } from 'pinia';
import { ref } from 'vue';

import * as configApi from '@/api/apiClient/config';

export const useConfigStore = defineStore('config', () => {
  const isPrivateInstance = ref(false);
  const configLoaded = ref(false);
  const namespace = ref<string | null>(null);
  const organizationName = ref('Organization');
  const orgCreatedLogo = ref<string | null>(null);
  const orgReviewedLogo = ref<string | null>(null);
  const skillGenEnabled = ref(false);
  const teamTranscriptViewingEnabled = ref(false);
  const noriSessionsEnabled = ref(false);
  const sentryDsn = ref('');
  const deploymentId = ref('');

  /**
   * Fetch the instance configuration
   */
  const fetchConfig = async () => {
    try {
      const config = await configApi.fetchConfig();
      isPrivateInstance.value = config.isPrivateInstance;
      namespace.value = config.namespace ?? null;
      organizationName.value = config.organizationName ?? 'Organization';
      orgCreatedLogo.value = config.orgCreatedLogo ?? null;
      orgReviewedLogo.value = config.orgReviewedLogo ?? null;
      skillGenEnabled.value = config.skillGenEnabled === true;
      teamTranscriptViewingEnabled.value =
        config.teamTranscriptViewingEnabled === true;
      noriSessionsEnabled.value = config.noriSessionsEnabled === true;
      sentryDsn.value = config.sentryDsn ?? '';
      deploymentId.value = config.deploymentId ?? '';
      configLoaded.value = true;
    } catch {
      // Default to false on error
      isPrivateInstance.value = false;
      namespace.value = null;
      organizationName.value = 'Organization';
      orgCreatedLogo.value = null;
      orgReviewedLogo.value = null;
      skillGenEnabled.value = false;
      teamTranscriptViewingEnabled.value = false;
      noriSessionsEnabled.value = false;
      sentryDsn.value = '';
      deploymentId.value = '';
      configLoaded.value = true;
    }
  };

  /**
   * Get the namespaced name for a skill or profile.
   * Returns "{namespace}/{name}" if namespace is set, otherwise just "{name}".
   *
   * @param args - The arguments object
   * @param args.name - The name of the skill or profile
   *
   * @returns The namespaced name or plain name
   */
  const getNamespacedName = (args: { name: string }): string => {
    const { name } = args;
    if (namespace.value != null) {
      return `${namespace.value}/${name}`;
    }
    return name;
  };

  return {
    isPrivateInstance,
    configLoaded,
    namespace,
    organizationName,
    orgCreatedLogo,
    orgReviewedLogo,
    skillGenEnabled,
    teamTranscriptViewingEnabled,
    noriSessionsEnabled,
    sentryDsn,
    deploymentId,
    fetchConfig,
    getNamespacedName,
  };
});
